import AOS from "aos";
import "aos/dist/aos.css";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";
import axios from "axios";
import { FaCheck } from "react-icons/fa";
import SearchableDropdown from "../../../componenets/SearchableDropdown";

function AddProjectContainer() {
    const { user, authToken } = useAuth();
    const [formData, setFormData] = useState({
        projectTitle: "",
        category: {
            departmental: false,
            interdisciplinary: false,
            internal: false,
            external: false,
        },
        objectives: "",
        teamMembers: [],
        domain: {
            preference1: {
                name: "",
                department: "",
                thrustArea: "",
            },
        },
        guide: {
            preference1: { email: "", guideId: "" },
            preference2: { email: "", guideId: "" },
            preference3: { email: "", guideId: "" },
            preference4: { email: "", guideId: "" },
        },
        industryExpert: {
            id: "",
            name: "",
            email: "",
            company: "",
            designation: "",
        },
        coGuides: {
            preference1: { email: "", guideId: "" },
            preference2: { email: "", guideId: "" },
            preference3: { email: "", guideId: "" },
            preference4: { email: "", guideId: "" },
        },
        ideaInspiration: "",
        literatureSurvey: "",
        references: "",
        objectivesTerm1: "",
        objectivesTerm2: "",
    });

    const [rollNo, setRollNo] = useState("");
    const [displayMembers, setDisplayMembers] = useState([]);
    const navigate = useNavigate();
    const [guides, setGuides] = useState([]);
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [studentDetails, setStudentDetails] = useState(null);
    const [rollNoError, setRollNoError] = useState("");
    const [industryExperts, setIndustryExperts] = useState([]);
    const [expertsLoading, setExpertsLoading] = useState(true);
    const [expertsError, setExpertsError] = useState(null);
    const [checkingAllocation, setCheckingAllocation] = useState(true);

    const handleRollNoChange = async (e) => {
        const value = e.target.value;
        setRollNo(value);
        setRollNoError("");
        setStudentDetails(null);

        if (value.length === 11) {
            try {
                const response = await axios.get(
                    `${API_BASE_URL}/api/students/${value}`,
                    {
                        headers: {
                            Authorization: `Bearer ${user.token}`,
                        },
                    }
                );

                if (!response.data) {
                    throw new Error("Student not found");
                }

                const data = response.data;
                setStudentDetails(data);
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    setRollNoError("Student not found");
                } else {
                    setRollNoError(err.message || "Invalid roll number");
                }
                setStudentDetails(null);
            }
        }
    };

    const handleAddMember = () => {
        if (!studentDetails) {
            setRollNoError("Please enter a valid roll number");
            return;
        }

        if (formData.teamMembers.includes(studentDetails.rollNo.toString())) {
            setRollNoError("This student is already added to the team");
            return;
        }

        if (formData.teamMembers.length >= 4) {
            setRollNoError("Maximum 4 team members allowed");
            return;
        }

        // Check if student is already allocated to a project (Requirement 3)
        if (studentDetails.projectGroupId || studentDetails.isAllocated) {
            setRollNoError("This student is already part of another project group");
            return;
        }

        setFormData((prev) => ({
            ...prev,
            teamMembers: [
                ...prev.teamMembers,
                studentDetails.rollNo.toString(),
            ],
        }));

        setRollNo("");
        setStudentDetails(null);
    };

    useEffect(() => {
        AOS.init({ duration: 1000 });
    }, []);

    useEffect(() => {
        const fetchMemberDetails = async () => {
            const details = [];
            for (const rollNo of formData.teamMembers) {
                try {
                    const response = await axios.get(
                        `${API_BASE_URL}/api/students/${rollNo}`,
                        {
                            headers: {
                                Authorization: `Bearer ${user.token}`,
                            },
                        }
                    );
                    details.push(response.data);
                } catch (error) {
                    console.error("Error fetching student details:", error);
                }
            }
            setDisplayMembers(details);
        };

        fetchMemberDetails();
    }, [formData.teamMembers]);

    useEffect(() => {
        const fetchGuides = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/guides?limit=500`, {
                    headers: {
                        "Authorization": `Bearer ${user?.token}`,
                    },
                });

                setGuides(response.data.guides || []);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching guides:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        const fetchDomains = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/domains`, {
                    headers: {
                        "Authorization": `Bearer ${user?.token}`,
                    },
                });
                const data = response.data;
                const formattedDomains = data.map(d => ({
                    ...d,
                    fullDetails: `${d.department} | ${d.thrustArea}`
                }));
                setDomains(formattedDomains);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching domains:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        const fetchIndustryExperts = async () => {
            try {
                setExpertsLoading(true);
                const response = await axios.get(`${API_BASE_URL}/api/experts?limit=1000`, {
                    headers: {
                        "Authorization": `Bearer ${user?.token}`,
                    },
                });
                const data = response.data;
                setIndustryExperts(data.experts || []);
                setExpertsLoading(false);
            } catch (err) {
                console.error("Error fetching industry experts:", err);
                setExpertsError(err.message);
                setExpertsLoading(false);
                toast.error("Failed to load industry experts. Using default data.");
            }
        };

        fetchGuides();
        fetchDomains();
        fetchIndustryExperts();
    }, []);

    // Auto-add current user
    useEffect(() => {
        const autoAddCurrentUser = async () => {
            if (!user || !user.email) {
                setCheckingAllocation(false);
                return;
            }

            try {
                // Fetch student details by email to get roll number and allocation status
                const response = await axios.get(`${API_BASE_URL}/api/students/email/${user.email}`,
                    {
                        headers: {
                            Authorization: `Bearer ${user.token}`,
                        },
                    }
                );
                if (response.status === 200) {
                    const data = response.data;
                    const userRollNo = data.rollNo;

                    // Check if student is already in a project
                    if (data.projectGroupId || data.isAllocated) {
                        navigate("/student/home");
                        return;
                    }

                    // Only add if not already in list
                    if (userRollNo) {
                        setFormData(prev => {
                            if (prev.teamMembers.includes(userRollNo.toString())) {
                                return prev;
                            }
                            return {
                                ...prev,
                                teamMembers: [...prev.teamMembers, userRollNo.toString()]
                            };
                        });
                    }
                }
            } catch (error) {
                console.error("Error auto-adding user:", error);
            } finally {
                setCheckingAllocation(false);
            }
        };

        if (user) {
            autoAddCurrentUser();
        } else {
            // If user is not yet available, wait (or handle if auth is required)
            // But usually this component is protected.
            // If we depend on 'user' from context, and it might be null initially:
            const timer = setTimeout(() => setCheckingAllocation(false), 2000); // Fail-safe
            return () => clearTimeout(timer);
        }
    }, [user, navigate]);



    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            category: { ...prev.category, [name]: checked },
        }));
    };

    const handleRemoveMember = (index) => {
        setFormData((prev) => ({
            ...prev,
            teamMembers: prev.teamMembers.filter((_, i) => i !== index),
        }));
    };

    const handleGuideSelect = (e, field) => {
        const selectedGuide = guides.find((g) => g.email === e.target.value);

        setFormData((prevFormData) => {
            const updatedFormData = { ...prevFormData };

            if (field.startsWith("guide")) {
                updatedFormData.guide[field.split(".")[1]] = {
                    email: selectedGuide?.email || "",
                    guideId: selectedGuide?._id || "",
                };
            } else {
                updatedFormData.coGuides[field.split(".")[1]] = {
                    email: selectedGuide?.email || "",
                    guideId: selectedGuide?._id || "",
                };
            }

            return updatedFormData;
        });
    };

    const handleIndustryExpertSelect = (e) => {
        const selectedExpert = industryExperts.find(
            (expert) => expert._id === e.target.value
        );

        setFormData((prev) => ({
            ...prev,
            industryExpert: selectedExpert
                ? {
                    id: selectedExpert._id,
                    name: selectedExpert.name,
                    email: selectedExpert.email,
                    company: selectedExpert.industry || selectedExpert.company || "",
                    designation: selectedExpert.designation || "",
                }
                : { id: "", name: "", email: "", company: "", designation: "" },
        }));
    };

    const countWords = (str) => {
        return str.trim().split(/\s+/).filter(word => word.length > 0).length;
    };

    const validateForm = () => {
        // console.log("Validating form...", formData);

        if (!formData.projectTitle.trim()) {
            toast.error("Please enter project title");
            return false;
        }

        if (countWords(formData.projectTitle) > 20) {
            toast.error("Project Title should not be more than 20 words");
            return false;
        }

        // Check if exactly one option is selected from each category group
        const typeSelected =
            formData.category.departmental ||
            formData.category.interdisciplinary;
        const sourceSelected =
            formData.category.internal ||
            formData.category.external;

        if (!typeSelected) {
            toast.error(
                "Please select either Departmental or Interdisciplinary"
            );
            return false;
        }

        if (!sourceSelected) {
            toast.error("Please select either Internal or External");
            const element = document.getElementById("project-source-section");
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return false;
        }

        // Validate Industry Expert for External Projects
        if (formData.category.external) {
            if (!formData.industryExpert || !formData.industryExpert.name) {
                toast.error("Industry Expert is required for External projects");
                const element = document.getElementById("industry-expert-section");
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return false;
            }
        }

        if (!formData.objectives.trim()) {
            toast.error("Please enter objectives");
            return false;
        }

        const objectivesWordCount = countWords(formData.objectives);
        if (objectivesWordCount < 10 || objectivesWordCount > 500) {
            toast.error("Objectives should be between 10 and 500 words");
            return false;
        }

        if (formData.teamMembers.length === 0) {
            toast.error("Please add at least one team member");
            return false;
        }

        if (!formData.domain.preference1.name) {
            toast.error("Please select a domain");
            return false;
        }

        if (!formData.guide.preference1.email) {
            toast.error("Please select Guide Preference 1");
            return false;
        }

        if (!formData.guide.preference2.email) {
            toast.error("Please select Guide Preference 2");
            return false;
        }

        if (!formData.coGuides.preference1.email) {
            toast.error("Please select Co-Guide Preference 1");
            return false;
        }

        if (!formData.coGuides.preference2.email) {
            toast.error("Please select Co-Guide Preference 2");
            return false;
        }

        if (!formData.ideaInspiration.trim()) {
            toast.error("Please provide idea inspiration");
            return false;
        }

        const ideaWordCount = countWords(formData.ideaInspiration);
        if (ideaWordCount < 10 || ideaWordCount > 500) {
            toast.error("Idea and Inspiration should be between 10 and 500 words");
            return false;
        }

        if (!formData.literatureSurvey) {
            toast.error("Please provide a valid URL for literature survey");
            return false;
        }

        const litSurveyLink = formData.literatureSurvey.trim();
        if (!litSurveyLink.startsWith("https://drive.google.com/drive/folders") || !litSurveyLink.endsWith("?usp=sharing")) {
            toast.error("Enter a valid Literature Survey link and make sure you made it accessible to Somaiya Vidyavihar University or anyone with link");
            return false;
        }

        if (!formData.references.trim()) {
            toast.error("Please provide references");
            return false;
        }

        if (
            !formData.objectivesTerm1.trim() ||
            !formData.objectivesTerm2.trim()
        ) {
            toast.error("Please provide objectives for both terms");
            return false;
        }

        const objTerm1WordCount = countWords(formData.objectivesTerm1);
        if (objTerm1WordCount < 10 || objTerm1WordCount > 500) {
            toast.error("Objectives for Term 1 should be between 10 and 500 words");
            return false;
        }

        const objTerm2WordCount = countWords(formData.objectivesTerm2);
        if (objTerm2WordCount < 10 || objTerm2WordCount > 500) {
            toast.error("Objectives for Term 2 should be between 10 and 500 words");
            return false;
        }

        // console.log("Form validation passed");
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // console.log("Form submission started");

        // Confirmation before validating/submitting
        const confirmed = window.confirm(
            "Are you sure you want to submit?"
        );
        if (!confirmed) {
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            if (!user || !user.userId) {
                toast.error(
                    "User information not available. Please login again."
                );
                navigate("/");
                return;
            }

            // console.log("User from Auth Context:", user);

            // Create a comprehensive formatted data object with all form fields
            const formattedData = {
                projectTitle: formData.projectTitle,
                category: formData.category,
                objectives: formData.objectives,
                teamMembers: formData.teamMembers,
                domain: {
                    preference1: {
                        name: formData.domain.preference1.name || "",
                        department:
                            formData.domain.preference1.department || "",
                        thrustArea:
                            formData.domain.preference1.thrustArea || "",
                    },
                },
                guide: {
                    preference1: {
                        email: formData.guide.preference1.email || "",
                        guideId: formData.guide.preference1.guideId || "",
                    },
                    preference2: {
                        email: formData.guide.preference2.email || "",
                        guideId: formData.guide.preference2.guideId || "",
                    },
                    preference3: {
                        email: formData.guide.preference3.email || "",
                        guideId: formData.guide.preference3.guideId || "",
                    },
                    preference4: {
                        email: formData.guide.preference4.email || "",
                        guideId: formData.guide.preference4.guideId || "",
                    },
                },
                coGuides: {
                    preference1: {
                        email: formData.coGuides.preference1.email || "",
                        guideId: formData.coGuides.preference1.guideId || "",
                    },
                    preference2: {
                        email: formData.coGuides.preference2.email || "",
                        guideId: formData.coGuides.preference2.guideId || "",
                    },
                    preference3: {
                        email: formData.coGuides.preference3.email || "",
                        guideId: formData.coGuides.preference3.guideId || "",
                    },
                    preference4: {
                        email: formData.coGuides.preference4.email || "",
                        guideId: formData.coGuides.preference4.guideId || "",
                    },
                },
                industryExpert: {
                    id: formData.industryExpert.id || "",
                    name: formData.industryExpert.name || "",
                    email: formData.industryExpert.email || "",
                    company: formData.industryExpert.company || "",
                    designation: formData.industryExpert.designation || "",
                },
                ideaInspiration: formData.ideaInspiration,
                literatureSurvey: formData.literatureSurvey,
                references: formData.references,
                objectivesTerm1: formData.objectivesTerm1,
                objectivesTerm2: formData.objectivesTerm2,
                userId: user.userId,
            };

            // console.log("Sending data:", formattedData);

            const response = await axios.post(
                `${API_BASE_URL}/api/projects/add`,
                formattedData,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken || user?.token || ""}`,
                    },
                }
            );

            // console.log("Server response:", response.data);
            toast.success("Project added successfully");
            navigate("/student/home");
        } catch (error) {
            console.error("Submission error:", error);

            if (error.response) {
                // The request was made and the server responded with an error
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);

                // Check if we have validation errors from the server
                if (error.response.data && error.response.data.errors) {
                    // Display each validation error
                    error.response.data.errors.forEach((err) => {
                        toast.error(`Validation error: ${err}`);
                    });
                } else {
                    // Display the general error message
                    const errorMessage =
                        error.response.data?.message ||
                        error.message ||
                        "Failed to create project";
                    toast.error(`Error adding project: ${errorMessage}`);
                }
            } else if (error.request) {
                // The request was made but no response was received
                toast.error(
                    "No response received from server. Please try again."
                );
            } else {
                // Something happened in setting up the request that triggered an Error
                toast.error(`Error adding project: ${error.message}`);
            }
        }
    };

    const getDisabledOptions = (currentField) => {
        const selectedEmails = new Set();

        // Collect from Guide Preferences
        Object.entries(formData.guide).forEach(([key, value]) => {
            if (value.email && `guide.${key}` !== currentField) {
                selectedEmails.add(value.email);
            }
        });

        // Collect from Co-Guide Preferences
        Object.entries(formData.coGuides).forEach(([key, value]) => {
            if (value.email && `coGuides.${key}` !== currentField) {
                selectedEmails.add(value.email);
            }
        });

        return selectedEmails;
    };

    if (loading || checkingAllocation) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 font-medium">Loading project details...</p>
            </div>
        );
    }

    return (
        <form
            className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8 my-12"
            onSubmit={handleSubmit}
        >
            <h2
                data-aos="fade-right"
                className="sm:text-3xl text-2xl  font-bold text-gray-800 border-b border-gray-200 pb-4 mb-6"
            >
                Enter Project Details
            </h2>

            <div className="space-y-2">
                <label className="block text-xl font-semibold text-gray-800">Project Title <span className="text-red-500">*</span></label>
                <input
                    type="text"
                    name="projectTitle"
                    value={formData.projectTitle}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#b7202e] focus:ring-2 focus:ring-[#b7202e]/20 outline-none transition-all duration-200 bg-gray-50 text-gray-800"
                />
            </div>

            <div className="space-y-6">
                <label className="block text-xl font-semibold text-gray-800">Category <span className="text-red-500">*</span></label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Project Type */}
                    <div className="space-y-3">
                        <div className="font-medium text-gray-600">Project Type:<span className="text-red-500">*</span></div>
                        <div className="flex flex-wrap gap-4">
                            {["departmental", "interdisciplinary"].map((key) => {
                                const isChecked = formData.category[key];
                                return (
                                    <div
                                        key={key}
                                        className="flex items-center gap-3 cursor-pointer group select-none"
                                        onClick={() => {
                                            if (!isChecked) {
                                                const event = { target: { name: key, checked: true } };
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    category: {
                                                        ...prev.category,
                                                        departmental: key === "departmental",
                                                        interdisciplinary: key === "interdisciplinary",
                                                    },
                                                }));
                                            }
                                        }}
                                    >
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 ${isChecked ? 'bg-[#b7202e] border-[#b7202e]' : 'bg-white border-gray-400 group-hover:border-[#b7202e]'}`}>
                                            <FaCheck className={`text-white text-xs transform transition-transform duration-200 ${isChecked ? 'scale-100' : 'scale-0'}`} />
                                        </div>
                                        <span className={`text-gray-700 font-medium capitalize group-hover:text-[#b7202e] transition-colors ${isChecked ? 'text-[#b7202e]' : ''}`}>
                                            {key}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Project Source */}
                    <div className="space-y-3" id="project-source-section">
                        <div className="font-medium text-gray-600">Project Source <span className="text-red-500">*</span></div>
                        <div className="flex flex-wrap gap-4">
                            {["internal", "external"].map((key) => {
                                const isChecked = formData.category[key];
                                return (
                                    <div
                                        key={key}
                                        className="flex items-center gap-3 cursor-pointer group select-none"
                                        onClick={() => {
                                            if (!isChecked) {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    category: {
                                                        ...prev.category,
                                                        internal: key === "internal",
                                                        external: key === "external",
                                                    },
                                                }));
                                            }
                                        }}
                                    >
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 ${isChecked ? 'bg-[#b7202e] border-[#b7202e]' : 'bg-white border-gray-400 group-hover:border-[#b7202e]'}`}>
                                            <FaCheck className={`text-white text-xs transform transition-transform duration-200 ${isChecked ? 'scale-100' : 'scale-0'}`} />
                                        </div>
                                        <span className={`text-gray-700 font-medium capitalize group-hover:text-[#b7202e] transition-colors ${isChecked ? 'text-[#b7202e]' : ''}`}>
                                            {key}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            <div className="space-y-2">
                <label className="block text-xl font-semibold text-gray-800">Objectives <span className="text-red-500">*</span></label>
                <textarea
                    name="objectives"
                    value={formData.objectives}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#b7202e] focus:ring-2 focus:ring-[#b7202e]/20 outline-none transition-all duration-200 bg-gray-50 text-gray-800 resize-y"
                />
            </div>

            <div className="space-y-4">
                <div className="flex justify-between items-end border-b pb-2">
                    <label className="text-xl font-semibold text-gray-800">Team Members <span className="text-red-500">*</span></label>
                    <div className="text-sm text-gray-500 font-medium bg-gray-100 px-3 py-1 rounded-full">
                        {formData.teamMembers.length}/4 members
                    </div>
                </div>

                <div className="space-y-3 bg-gray-50 p-6 rounded-xl border border-gray-200">
                    <label className="text-gray-700 font-medium">Add Student by Roll No.</label>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="text"
                            value={rollNo}
                            onChange={handleRollNoChange}
                            maxLength="11"
                            placeholder="Enter 11-digit Roll No."
                            disabled={formData.teamMembers.length >= 4}
                            className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:border-[#b7202e] focus:ring-2 focus:ring-[#b7202e]/20 outline-none transition-all disabled:opacity-60 disabled:bg-gray-200"
                        />
                        <button
                            type="button"
                            onClick={handleAddMember}
                            disabled={formData.teamMembers.length >= 4}
                            className="px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none whitespace-nowrap"
                        >
                            Add Member
                        </button>
                    </div>

                    <div>
                        {rollNoError && (
                            <div className="text-red-500 text-sm flex items-center gap-1">
                                <span>⚠️</span> {rollNoError}
                            </div>
                        )}
                        {studentDetails && !rollNoError && (
                            <div className="text-green-600 text-sm font-medium flex items-center gap-1">
                                <span>✅</span> Found: {studentDetails.name} - {studentDetails.department}
                            </div>
                        )}
                    </div>
                </div>

                {displayMembers.length > 0 && (
                    <div className="space-y-3 mt-4">
                        <h4 className="text-gray-700 font-semibold text-sm uppercase tracking-wide">Current Team</h4>
                        {displayMembers.map((member, index) => (
                            <div
                                key={member.rollNo}
                                className="flex justify-between items-center p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="flex flex-col">
                                    <span className="font-semibold text-gray-800">{member.name}</span>
                                    <span className="text-sm text-gray-500">{member.rollNo} • {member.department}</span>
                                </div>
                                {member.email !== user?.email && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleRemoveMember(index);
                                            setDisplayMembers((prev) =>
                                                prev.filter((_, i) => i !== index)
                                            );
                                        }}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="space-y-2">
                <label className="block text-xl font-semibold text-gray-800">Choose Domain <span className="text-red-500">*</span></label>
                <div className="relative">
                    <SearchableDropdown
                        options={domains}
                        value={domains.find(d => d.name === formData.domain.preference1.name)?._id || ""}
                        onChange={(e) => {
                            const selectedDomain = domains.find(
                                (d) => d._id === e.target.value
                            );
                            setFormData((prev) => ({
                                ...prev,
                                domain: {
                                    preference1: {
                                        name: selectedDomain?.name || "",
                                        department: selectedDomain?.department || "",
                                        thrustArea: selectedDomain?.thrustArea || "",
                                    },
                                },
                            }));
                        }}
                        placeholder="Select Your Domain"
                        labelKey="name"
                        valueKey="_id"
                        subLabelKey="fullDetails"
                        required
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Guide Preferences */}
                <div className="space-y-4">
                    <label className="block text-xl font-semibold text-gray-800">Guide Preferences <span className="text-red-500">*</span></label>
                    <div className="space-y-4">
                        {["preference1", "preference2", "preference3", "preference4"].map((key, index) => {
                            const currentField = `guide.${key}`;
                            const disabledOptions = getDisabledOptions(currentField);

                            return (
                                <div key={index} className="space-y-1">
                                    <label className="text-sm font-medium text-gray-600">Preference {index + 1}<span className="text-red-500">{index <= 1 ? " *" : ""}</span></label>
                                    <div className="relative">
                                        <SearchableDropdown
                                            options={guides}
                                            value={formData.guide[key].email || ""}
                                            onChange={(e) => handleGuideSelect(e, currentField)}
                                            placeholder="Select Guide"
                                            labelKey="name"
                                            valueKey="email"
                                            subLabelKey="department"
                                            disabledOptions={disabledOptions}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Co-Guide Preferences */}
                <div className="space-y-4">
                    <label className="block text-xl font-semibold text-gray-800">Co-guide Preferences <span className="text-red-500">*</span></label>
                    <div className="space-y-4">
                        {["preference1", "preference2", "preference3", "preference4"].map((key, index) => {
                            const currentField = `coGuides.${key}`;
                            const disabledOptions = getDisabledOptions(currentField);

                            return (
                                <div key={index} className="space-y-1">
                                    <label className="text-sm font-medium text-gray-600">Preference {index + 1}<span className="text-red-500">{index <= 1 ? " *" : ""}</span></label>
                                    <div className="relative">
                                        <SearchableDropdown
                                            options={guides}
                                            value={formData.coGuides[key].email || ""}
                                            onChange={(e) => handleGuideSelect(e, currentField)}
                                            placeholder="Select Co-Guide"
                                            labelKey="name"
                                            valueKey="email"
                                            subLabelKey="department"
                                            disabledOptions={disabledOptions}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="space-y-2" id="industry-expert-section">
                <label className="block text-xl font-semibold text-gray-800">Industry Expert {formData.category.external && <span className="text-red-500">*</span>}</label>
                {expertsLoading ? (
                    <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg animate-pulse">Loading industry experts...</div>
                ) : expertsError ? (
                    <div className="p-4 text-center text-red-500 bg-red-50 rounded-lg border border-red-100">
                        Error loading industry experts: {expertsError}
                    </div>
                ) : (
                    <div className="relative">
                        <SearchableDropdown
                            options={industryExperts}
                            value={formData.industryExpert.id || ""}
                            onChange={handleIndustryExpertSelect}
                            placeholder="Select Industry Expert"
                            labelKey="name"
                            valueKey="_id"
                            subLabelKey="company"
                        />
                    </div>
                )}
            </div>

            <div className="space-y-4">
                <div className="space-y-2">
                    <label className="block text-xl font-semibold text-gray-800">Idea Inspiration <span className="text-red-500">*</span></label>
                    <textarea
                        name="ideaInspiration"
                        value={formData.ideaInspiration}
                        onChange={handleInputChange}
                        required
                        rows="3"
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#b7202e] focus:ring-2 focus:ring-[#b7202e]/20 outline-none transition-all duration-200 bg-gray-50 text-gray-800"
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-xl font-semibold text-gray-800">Literature Survey Link <span className="text-red-500">*</span></label>
                    <input
                        type="text"
                        name="literatureSurvey"
                        value={formData.literatureSurvey}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter the folder link of the Literature Survey and add documents there"
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#b7202e] focus:ring-2 focus:ring-[#b7202e]/20 outline-none transition-all duration-200 bg-gray-50 text-gray-800"
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-xl font-semibold text-gray-800">References <span className="text-red-500">*</span></label>
                    <textarea
                        name="references"
                        value={formData.references}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter the references in format: Title - Link"
                        rows="3"
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#b7202e] focus:ring-2 focus:ring-[#b7202e]/20 outline-none transition-all duration-200 bg-gray-50 text-gray-800"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                    <label className="block text-xl font-semibold text-gray-800">Objectives for 6th sem <span className="text-red-500">*</span></label>
                    <textarea
                        name="objectivesTerm1"
                        value={formData.objectivesTerm1}
                        onChange={handleInputChange}
                        required
                        rows="4"
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#b7202e] focus:ring-2 focus:ring-[#b7202e]/20 outline-none transition-all duration-200 bg-gray-50 text-gray-800"
                    />
                </div>

                <div className="space-y-2">
                    <label className="block text-xl font-semibold text-gray-800">Objectives for 7th sem <span className="text-red-500">*</span></label>
                    <textarea
                        name="objectivesTerm2"
                        value={formData.objectivesTerm2}
                        onChange={handleInputChange}
                        required
                        rows="4"
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#b7202e] focus:ring-2 focus:ring-[#b7202e]/20 outline-none transition-all duration-200 bg-gray-50 text-gray-800"
                    />
                </div>
            </div>

            <div className="pt-6">
                <button
                    type="submit"
                    className="w-full bg-[#b7202e] text-white font-bold py-4 px-8 rounded-xl hover:bg-[#8e1924] hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 focus:ring-4 focus:ring-[#b7202e]/40 shadow-md text-lg"
                >
                    Submit Project Proposal
                </button>
            </div>
        </form>
    );
}

export default AddProjectContainer;
